import {
  BuildJobSortBySchema,
  BuildJobStatus,
  BuildType,
  GetBuildJobResponse,
  GetBuildJobResponseSchema,
  GetBuildUrlResponseSchema,
  ListBuildJobsRequestSchema,
  ListBuildJobsResponse,
  ListBuildJobsResponseSchema,
  SortOrderSchema,
  StartBuildJobRequestSchema,
  StartBuildJobResponse,
  StartBuildJobResponseSchema,
} from "../../darvin-shared/schema/zod/build";
import {
  HttpFailedToFetchException,
  HttpNotEnoughTokensException,
} from "../model/httpException";
import { getAuthHeaders } from "../utils/authHeaders";
import { logException } from "../utils/logUtils";
import { fetchWithRetry } from "../utils/retry.util";

export class BuildApi {
  private static readonly BASE_URL = import.meta.env.VITE_API_URL;
  private static readonly BASE_ENDPOINT = `build`;

  static async startBuild(
    projectId: string,
    buildType: BuildType,
    revision?: string
  ): Promise<StartBuildJobResponse> {
    const body = StartBuildJobRequestSchema.parse({
      buildType,
      revision,
    });

    const headers = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetchWithRetry(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Build API Error (build/${projectId}):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      if (response.status === 402) {
        throw new HttpNotEnoughTokensException();
      } else {
        throw new HttpFailedToFetchException();
      }
    }

    const data = await response.json();
    return StartBuildJobResponseSchema.parse(data);
  }

  static async loadBuilds({
    projectId,
    startAfterBuildId,
    buildTypes,
    statuses,
    limit = 10,
  }: {
    projectId: string;
    startAfterBuildId?: string;
    buildTypes: BuildType[];
    statuses?: BuildJobStatus[];
    limit?: number;
  }): Promise<ListBuildJobsResponse> {
    const params = ListBuildJobsRequestSchema.parse({
      limit: limit,
      ...(startAfterBuildId && { startAfter: startAfterBuildId }),
      sortBy: BuildJobSortBySchema.enum.update,
      sortOrder: SortOrderSchema.enum.desc,
    });

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    buildTypes.forEach((buildType) => {
      queryParams.append("buildTypes[]", buildType);
    });

    if (statuses) {
      statuses.forEach((status) => {
        queryParams.append("statuses[]", status);
      });
    }

    const headers = await getAuthHeaders({});

    const response = await fetchWithRetry(
      `${this.BASE_URL}/${
        this.BASE_ENDPOINT
      }/${projectId}/jobs?${queryParams.toString()}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Build API Error (build/${projectId}/jobs):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    const data = await response.json();
    return ListBuildJobsResponseSchema.parse(data);
  }

  static async loadSingleBuild(
    projectId: string,
    buildId: string
  ): Promise<GetBuildJobResponse> {
    const headers = await getAuthHeaders({});

    const response = await fetchWithRetry(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}/jobs/${buildId}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Build API Error (build/${projectId}/jobs/${buildId}):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    const data = await response.json();
    return GetBuildJobResponseSchema.parse(data);
  }

  static async loadUrl(projectId: string, buildId: string): Promise<string> {
    const headers = await getAuthHeaders({});

    const response = await fetchWithRetry(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}/jobs/${buildId}/url`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(
        `Build API Error (build/${projectId}/jobs/${buildId}/url):`,
        {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        }
      );
      throw new HttpFailedToFetchException();
    }

    const data = await response.json();
    return GetBuildUrlResponseSchema.parse(data).buildUrl;
  }
}
